import { useState } from 'react';
import { Box, Stack } from '@mui/material';
import {
  CasBaseDialog,
  CasChip,
  CasDisplayField,
  CasLoaderWithBackdrop,
  CasStackRow,
  CasTabsView,
} from 'src/compass/shared/components';
import { DateFormat, formatDate } from 'src/compass/shared/util/casMoment';
import { useRetrieveDetailsById } from 'src/compass/shared/hooks/reactQuery/useRetrieveDetailsById';
import { AccountsTab, OwnersTab, ContactsTab } from 'src/compass/application';
import { Terms } from 'src/compass/shared/util/terms';
import HubspotLink from 'src/compass/application/entityMaintenance/HubspotLink';
import CasLink from 'src/compass/shared/components/CasLink/CasLink';
import useCasBoolean from 'src/compass/shared/hooks/useCasBoolean';
import RenderTouchpointFrequency, { useTouchpointCache } from './RenderTouchpointFrequency';
import RMCOfRecordDialog, { RiskManagerType } from './RMCOfRecordDialog';
import TagChips from './TagChips';

interface IProps {
  id: number;
  onClose: (dataUpdated?: boolean) => void;
  // The tab to open on, by label; the first (Contacts) when omitted.
  defaultTab?: 'Contacts' | 'Accounts' | 'Owners';
}

export default function CustomerDialog({ id, onClose, defaultTab }: IProps) {
  const { updateTouchpointDetails } = useTouchpointCache();
  const [dataUpdated, setDataUpdated] = useState<boolean>(false);
  const rmcOfRecordDialog = useCasBoolean();

  const { data, isLoading, isRefetching, isError, refetch } = useRetrieveDetailsById({
    entity: 'Utilities/EntityTouchpoint',
    id,
  });

  if (isLoading) return <CasLoaderWithBackdrop />;
  if (isError) throw new Error('Error retrieving data from the server.');

  const tabList = [
    { label: 'Contacts', children: <ContactsTab entityId={id} data={data?.contacts} onUpdated={refetch} /> },
    { label: 'Accounts', children: <AccountsTab entityId={id} readOnly /> },
    { label: 'Owners', children: <OwnersTab entityId={id} readOnly /> },
  ];

  return (
    <CasBaseDialog
      fullScreen
      maxWidth="xl"
      open
      onClose={() => onClose(dataUpdated)}
      title={
        data && (
          <Stack spacing={0.5}>
            <span>{`${Terms.Customer.label}: ${data?.name}`}</span>
            <TagChips
              entityId={id}
              tags={data?.tags ?? []}
              onChanged={() => setDataUpdated(true)}
            />
          </Stack>
        )
      }
      showCloseIcon
    >
      {isRefetching && <CasLoaderWithBackdrop /> }
      {/* An inactive customer can still be opened - from the search box, or the Customers tab's
          Selection filter - so the dialog says so before anything else. */}
      {data?.inactive && (
        <Box
          sx={{
            bgcolor: 'error.main',
            color: 'error.contrastText',
            px: 1.5,
            py: 0.5,
            mb: 1,
            borderRadius: 1,
            typography: 'subtitle2',
          }}
        >
          Inactive
        </Box>
      )}
      <CasStackRow>
        <CasDisplayField
          label="Next Touchpoint"
          value={
            data?._isPending ? '(updating)' : formatDate(data?.nextTouchpointDate, DateFormat.MDY)
          }
        />
        <CasDisplayField
          label="Frequency"
          value={
            <RenderTouchpointFrequency
              id={id}
              defaultValue={data.touchpointFrequency}
              onUpdated={(nextTouchpointDate) => {
                updateTouchpointDetails({ id, nextTouchpointDate });
                setDataUpdated(true);
              }}
            />
          }
        />
        <CasDisplayField
          label="Last Touchpoint"
          value={formatDate(data?.lastTouchpointDate, DateFormat.MDY)}
        />
        <CasDisplayField
          label="Service Owner"
          value={
            <CasLink onClick={rmcOfRecordDialog.setTrue}>{data?.rmcOfRecordName ?? 'None'}</CasLink>
          }
        />
        <CasDisplayField
          label="Risk Managers"
          value={
            <CasStackRow>
              {/* Light yellow marks the Service Owner, as in the listing's Name column. */}
              {data?.riskManagers?.map((riskManager: RiskManagerType) => (
                <CasChip
                  key={riskManager.id}
                  label={riskManager.name}
                  color={riskManager.id === data?.rmcOfRecordEmployeeId ? 'warning' : 'disabled'}
                />
              ))}
            </CasStackRow>
          }
        />
      </CasStackRow>
      {data?.companyName && (
        <CasDisplayField
          label={
            <CasStackRow alignItems="center">
              <HubspotLink id={data?.companyId} recordPath="record/0-2" />
              {Terms.CRM.label} Company
            </CasStackRow>
          }
          value={data.companyName}
        />
      )}
      {data?.hasHandoffRequest && (
        <Box
          sx={{
            backgroundColor: '#a9ffa9',
            width: 'fit-content',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: '0.875rem',
          }}
        >
          {`Handoff req by ${data?.handoffRequestedByName}`}
        </Box>
      )}
      <CasTabsView tabList={tabList} defaultActiveTab={defaultTab} />
      {rmcOfRecordDialog.value && (
        <RMCOfRecordDialog
          entityId={id}
          employeeId={data?.rmcOfRecordEmployeeId}
          riskManagers={data?.riskManagers ?? []}
          onClose={rmcOfRecordDialog.setFalse}
          onSaved={() => {
            rmcOfRecordDialog.setFalse();
            refetch();
            // The listing's chips are colored by Service Owner, so it reloads on close.
            setDataUpdated(true);
          }}
        />
      )}
    </CasBaseDialog>
  );
}
